/**
 * Single source for /about crawler + hydrated SEO copy (REN-213).
 *
 * Imported by:
 * - `pages/About.tsx` (MetaTags + hero lede phrases)
 * - `scripts/prerender-public-routes.ts` (dist/about/index.html)
 *
 * Crawler bodyHtml is an intentional short excerpt for scrapers — not a full
 * mirror of the editorial layout — but every phrase here must stay in sync
 * with the live About route.
 */

export const ABOUT_SEO = {
  title: 'About — rendezvous',
  description:
    'It started with a quiet question: where do I go to find my people now? The story of rendezvous — community over clout, vibes not résumés.',
  image: 'https://rendezvouswebsite.b-cdn.net/DSCF9542.JPG',
  url: 'https://letsrendezvous.co/about',
} as const;

/** Phrases that appear in both the hydrated About page and the prerender HTML. */
export const ABOUT_SHARED_COPY = {
  headlineLead: 'It started with a',
  headlineEm: 'quiet question.',
  lede: 'Where do I go to find my people now?',
  opener:
    'No Instagram page. No fancy logo. Two friends at inflection points, craving something real.',
  groupTrip:
    'So we did the scariest thing you can do as an adult: joined a group trip with strangers.',
  birthQuote: 'That first trip spiraled into something more — and rendezvous was born.',
  beliefs: [
    'Vibes, not résumés.',
    'Real connection.',
    'A global movement.',
    'No bullshit.',
  ],
} as const;

export type PublicPrerenderRoute = {
  path: string;
  title: string;
  description: string;
  image: string;
  url: string;
  bodyHtml: string;
};

/**
 * Compact HTML for non-JS agents. Keep phrases sourced from ABOUT_SHARED_COPY
 * so curl/og scrapers see the same story beats as the React route.
 */
export function buildAboutCrawlerBodyHtml(): string {
  const c = ABOUT_SHARED_COPY;
  return `
<article data-prerender="about">
  <h1>${c.headlineLead} ${c.headlineEm.replace(/\.$/, '')}</h1>
  <p>${c.lede}</p>
  <p>${c.opener}</p>
  <p>${c.groupTrip} And something clicked. ${c.birthQuote}</p>
  <p>${c.beliefs.join(' ')}</p>
</article>
`.trim();
}

export const ABOUT_PRERENDER_ROUTE: PublicPrerenderRoute = {
  path: 'about',
  title: ABOUT_SEO.title,
  description: ABOUT_SEO.description,
  image: ABOUT_SEO.image,
  url: ABOUT_SEO.url,
  bodyHtml: buildAboutCrawlerBodyHtml(),
};
